import type { Combatant, GameFinished, ServerMessage, ShotResolved, SkillTarget, SkillUsed, Team, TurnStarted } from '@ddt/protocol';
import { GameEndReason, SkillKind } from '@ddt/protocol';
import { aimAngle } from './aim';
import { shotReleaseMs } from './character-animator';
import { ClockSync } from './timing';

export interface CombatantView {
  accountId: string;
  nickName: string;
  team: Team;
  /** Posição autoritativa (a última que o servidor informou). */
  x: number;
  y: number;
  /** Posição desenhada: acompanha `x`/`y` com suavização. */
  displayX: number;
  displayY: number;
  blood: number;
  maxBlood: number;
  facing: number;
  alive: boolean;
  /** Desistiu da partida: o desenho mostra a saída de quem desiste, e não a queda de quem morre. */
  forfeited: boolean;
  /** Pontos de ação (PA) que sobram no turno; só vale para quem está na vez. */
  actionPoints: number;
  maxActionPoints: number;
  /** Quanto ainda anda (px) com o PA de movimento já pago. */
  moveAllowance: number;
  skills: SkillView[];
  effects: EffectView[];
  dander: number;
  connected: boolean;
  /** Ângulo do canhão em graus (0 = direita, anti-horário); local, o servidor só recebe o do tiro. */
  aim: number;
  /** Inclinação do chão sob o personagem em graus (positivo: sobe para a direita), medida pelo servidor: é o 0° da mira. */
  tilt: number;
  /** Faixa de mira da arma equipada: a elevação vai de 0° (a inclinação do chão) até este valor, para o lado em que ele olha. */
  angleRange: number;
  /** Visual do personagem (só desenho): sexo, lista de peças do Character e modelo da arma equipada (0 = sem arma). */
  sex: boolean;
  style: string;
  weaponTemplateId: number;
  /** Nível de fortalecimento da arma equipada: escolhe a imagem da arma por nível (ADR 0036). */
  weaponStrengthenLevel?: number;
  /** Bombas cujo desenho o estágio visual da arma troca (ADR 0032): a arte delas baixa antes do primeiro tiro. Vazio = o visual da arma não troca. */
  visualBallIds: number[];
  /** Brilho do boneco parado (roupa × 100 + arma): quais luzes a tela de confronto acende. */
  nimbus: number;
}

export interface SkillView {
  skillId: number;
  name: string;
  kind: SkillKind;
  target: SkillTarget;
  /** Turnos do próprio jogador até poder usar de novo (0 = pronta). */
  cooldownLeft: number;
  cooldownTurns: number;
  /** Custo em PA próprio da habilidade; 0 = o padrão de habilidade. */
  apCost: number;
}

export interface EffectView {
  skillId: number;
  kind: SkillKind;
  sourceId: string;
  /** Atributo alterado (buff/debuff); vazio nos demais. */
  attribute: string;
  /** Pontos (buff positivo, debuff negativo) ou fator de dano 0 a 1 (provocação). */
  amount: number;
  /** Turnos do próprio alvo que ainda faltam. */
  turnsLeft: number;
}

export interface CraterInfo {
  ballId: number;
  x: number;
  y: number;
}

export interface TurnInfo {
  number: number;
  accountId: string;
  wind: number;
  durationMs: number;
  /** Instante (ms Unix, relógio do servidor) em que o turno começa; pode estar no futuro durante a animação do tiro anterior. */
  startsAtMs: number;
}

export interface ShotPlayback {
  shot: ShotResolved;
  /** Instante local (ms) em que a reprodução começou. */
  startedAtMs: number;
  durationMs: number;
  /** O projétil já saiu da mão (passou o tempo de preparo). */
  released: boolean;
}

export type StateEvent =
  | { type: 'gameStarted' }
  | { type: 'turnStarted'; turn: TurnInfo }
  | { type: 'playerMoved'; accountId: string }
  | { type: 'skillUsed'; skill: SkillUsed }
  | { type: 'shotStarted'; playback: ShotPlayback }
  /** O projétil sai da mão do atirador, depois do gesto de preparo: é o momento do som do disparo. */
  | { type: 'shotReleased'; playback: ShotPlayback }
  | { type: 'impact'; playback: ShotPlayback; crater: CraterInfo | null }
  | { type: 'snapshot' }
  | { type: 'finished'; result: GameFinished };

export type GameStatus = 'idle' | 'playing' | 'finished';

/**
 * Velocidade da reprodução do voo do projétil em relação ao tempo do servidor (um ponto a cada 40 ms, os 25 quadros por segundo do legado).
 * 1,9 deixa o voo mais rápido que o legado (47,5 quadros por segundo equivalentes): o projétil percorre o mesmo caminho, só mais depressa. O servidor continua a decidir tudo.
 */
export const PROJECTILE_SPEED = 1.9;

/** Quem anda para o vazio cai só um pouco (`FALL_SHOW_PX`, em `FALL_DEATH_MS`) e vira fantasma; a cova dele é que segue caindo. */
export const FALL_DEATH_MS = 450;
export const FALL_SHOW_PX = 80;
/** Com o jogador já fantasma, quanto a tela espera antes de mostrar o resultado (o legado esperava as animações e mostrava o resultado em seguida). */
export const GHOST_HOLD_MS = 2500;
const SNAP_DISTANCE = 400;
const SMOOTHING_MS = 70;

/** Faixa da elevação da mira, em graus acima do chão, para o lado em que o jogador olha. O máximo real é o da arma (`angleRange`). */
export const AIM_ELEVATION_MIN = 0;
export const AIM_ELEVATION_MAX = 90;
/** Faixa de mira usada enquanto o servidor não informa a da arma. */
export const DEFAULT_ANGLE_RANGE = 50;
/** Custos em PA da tabela de física padrão, usados até o servidor informar os em vigor. */
export const DEFAULT_ATTACK_COST = 2;
export const DEFAULT_SKILL_COST = 1;

/**
 * Converte a elevação da mira (graus acima do chão, para o lado em que o jogador olha) no ângulo absoluto do protocolo (0 = direita, anti-horário).
 * O 0° da elevação é a inclinação do chão sob o personagem (`tilt`, positivo quando o chão sobe para a direita) e a elevação vai até `range`.
 * Em chão plano: olhando para a direita, 30° vira 30°; para a esquerda, 150°. Com `tilt` 14°: 14° + elevação à direita, 180° + 14° − elevação à esquerda.
 * O servidor recusa o tiro fora dessa faixa e o tiro para o lado oposto ao `facing`.
 */
export function absoluteAim(elevation: number, facing: number, tilt = 0, range = AIM_ELEVATION_MAX): number {
  return aimAngle(Math.min(range, Math.max(AIM_ELEVATION_MIN, elevation)), facing, tilt);
}

function toView(c: Combatant, aim = c.facing >= 0 ? 45 : 135): CombatantView {
  const x = c.position?.x ?? 0;
  const y = c.position?.y ?? 0;
  return {
    accountId: c.accountId,
    nickName: c.nickName,
    team: c.team,
    x,
    y,
    displayX: x,
    displayY: y,
    blood: c.blood,
    maxBlood: c.maxBlood,
    facing: c.facing,
    alive: c.alive,
    forfeited: c.forfeited,
    actionPoints: c.actionPoints,
    maxActionPoints: c.maxActionPoints,
    moveAllowance: c.moveAllowance,
    skills: c.skills.map((s) => ({ skillId: s.skillId, name: s.name, kind: s.kind, target: s.target, cooldownLeft: s.cooldownLeft, cooldownTurns: s.cooldownTurns, apCost: s.apCost })),
    effects: c.effects.map((f) => ({ skillId: f.skillId, kind: f.kind, sourceId: f.sourceId, attribute: f.attribute, amount: f.amount, turnsLeft: f.turnsLeft })),
    dander: c.dander,
    connected: c.connected,
    aim,
    tilt: c.tilt,
    angleRange: c.angleRange > 0 ? c.angleRange : DEFAULT_ANGLE_RANGE,
    sex: c.sex,
    style: c.style,
    weaponTemplateId: c.weaponTemplateId,
    weaponStrengthenLevel: c.weaponStrengthenLevel,
    visualBallIds: [...c.visualBallIds],
    nimbus: c.nimbus,
  };
}

/**
 * Estado da partida no cliente, alimentado só pelas mensagens do servidor (ele é a autoridade). A reprodução do tiro adia os efeitos
 * (dano, queda, cratera) até o fim da trajetória, para a tela contar a mesma história que a animação.
 */
export class GameState {
  status: GameStatus = 'idle';
  gameId = '';
  mapId = 0;
  mapWidth = 0;
  mapHeight = 0;
  gravity = 0;
  airResistance = 0;
  terrainHash = '';
  /** Custo em PA do tiro e de uma habilidade sem custo próprio (do servidor); valores padrão até ele informar. */
  attackCost = DEFAULT_ATTACK_COST;
  skillCost = DEFAULT_SKILL_COST;
  turn: TurnInfo | null = null;
  hasFired = false;
  /** Instante (ms Unix, relógio do servidor) até o qual o jogador da vez não pode agir porque o tiro dele ainda é reproduzido. */
  lockedUntilMs = 0;
  craters: CraterInfo[] = [];
  playback: ShotPlayback | null = null;
  result: GameFinished | null = null;
  readonly combatants = new Map<string, CombatantView>();
  readonly clock: ClockSync;
  private elapsedMs = 0;
  /** Resultado que o servidor já decidiu, mas a tela só mostra depois da animação do último golpe (ou da queda) e de um tempo com o fantasma na tela. */
  private pendingFinish: GameFinished | null = null;
  private finishAtMs = -1;
  /** Quem andou para o vazio: instante (tempo do estado) em que a queda acaba e ele vira fantasma. */
  private readonly falls = new Map<string, number>();

  /** `releaseDelayMs`: quanto o tiro da bomba espera para sair (o gesto de preparo do atirador); o impacto e o dano atrasam junto. */
  constructor(
    private readonly localNow: () => number = Date.now,
    private readonly releaseDelayMs: (ballId: number) => number = shotReleaseMs,
  ) {
    this.clock = new ClockSync(localNow);
  }

  get wind(): number {
    return this.turn?.wind ?? 0;
  }

  combatantList(): CombatantView[] {
    return [...this.combatants.values()];
  }

  me(accountId: string): CombatantView | undefined {
    return this.combatants.get(accountId);
  }

  /** O jogador pode agir agora: é a vez dele, o turno já começou, o tiro anterior (se houve) já terminou de ser reproduzido. */
  canAct(accountId: string): boolean {
    const turn = this.turn;
    if (!turn || this.status !== 'playing' || turn.accountId !== accountId || this.playback || this.pendingFinish) return false;
    if (this.lockedUntilMs > 0 && this.clock.until(this.lockedUntilMs) > 0) return false;
    return this.clock.until(turn.startsAtMs) <= 0 && this.turnRemainingMs() > 0 && this.combatants.get(accountId)?.alive === true;
  }

  /** Quanto falta para o fim do turno atual; durante a pausa do tiro anterior é a duração cheia. */
  turnRemainingMs(): number {
    const turn = this.turn;
    if (!turn) return 0;
    return Math.max(0, Math.min(turn.durationMs, turn.startsAtMs + turn.durationMs - this.clock.serverNow()));
  }

  /** Quanto falta para o turno começar (0 se já começou). */
  startsInMs(): number {
    return this.turn ? Math.max(0, this.clock.until(this.turn.startsAtMs)) : 0;
  }

  setAim(accountId: string, angle: number): void {
    const c = this.combatants.get(accountId);
    if (c) c.aim = angle;
  }

  apply(message: ServerMessage): StateEvent[] {
    const payload = message.payload;
    switch (payload.case) {
      case 'pong': {
        const pong = payload.value;
        this.clock.observe(Number(pong.clientTimeMs), Number(pong.serverTimeMs));
        return [];
      }
      case 'gameStarted': {
        const g = payload.value;
        this.reset();
        this.status = 'playing';
        this.gameId = g.gameId;
        this.mapId = g.mapId;
        this.mapWidth = g.mapWidth;
        this.mapHeight = g.mapHeight;
        this.gravity = g.gravity;
        this.airResistance = g.airResistance;
        this.terrainHash = g.terrainHash;
        this.attackCost = g.attackCost > 0 ? g.attackCost : DEFAULT_ATTACK_COST;
        this.skillCost = g.skillCost > 0 ? g.skillCost : DEFAULT_SKILL_COST;
        for (const c of g.combatants) this.combatants.set(c.accountId, toView(c));
        return [{ type: 'gameStarted' }];
      }
      case 'turnStarted': {
        this.advanceTurnTimers(payload.value.accountId);
        this.turn = this.toTurn(payload.value);
        this.hasFired = false;
        this.lockedUntilMs = 0;
        // O servidor devolve os PA ao jogador da vez; a mensagem do turno não os traz, mas o máximo é conhecido.
        const current = this.combatants.get(this.turn.accountId);
        if (current) {
          current.actionPoints = current.maxActionPoints;
          current.moveAllowance = 0;
        }
        return [{ type: 'turnStarted', turn: this.turn }];
      }
      case 'playerMoved': {
        const m = payload.value;
        const c = this.combatants.get(m.accountId);
        if (!c) return [];
        this.moveTo(c, m.position?.x ?? c.x, m.fell ? c.y + FALL_SHOW_PX : (m.position?.y ?? c.y));
        c.tilt = m.tilt;
        // Virou: o canhão acompanha, espelhado em torno do chão (a mesma elevação para o outro lado).
        if (m.facing !== c.facing) c.aim = 180 + 2 * c.tilt - c.aim;
        c.facing = m.facing;
        c.actionPoints = m.actionPoints;
        c.moveAllowance = m.moveAllowance;
        // Andou para o vazio: o servidor decidiu a morte; aqui o jogador cai e só vira fantasma no fim da queda.
        if (m.fell) this.falls.set(c.accountId, this.elapsedMs + FALL_DEATH_MS);
        return [{ type: 'playerMoved', accountId: c.accountId }];
      }
      case 'skillUsed': {
        const s = payload.value;
        const caster = this.combatants.get(s.casterId);
        const target = this.combatants.get(s.targetId);
        if (caster) {
          caster.actionPoints = s.actionPoints;
          caster.moveAllowance = s.moveAllowance;
          const skill = caster.skills.find((k) => k.skillId === s.skillId);
          if (skill) skill.cooldownLeft = s.cooldownTurns;
        }
        if (target) {
          if (s.kind === SkillKind.HEAL) target.blood = s.bloodAfter;
          if (s.kind === SkillKind.DASH) this.moveTo(target, s.position?.x ?? target.x, s.position?.y ?? target.y);
          if (s.kind === SkillKind.BUFF || s.kind === SkillKind.DEBUFF || s.kind === SkillKind.TAUNT) {
            target.effects = target.effects.filter((f) => !(f.skillId === s.skillId && f.sourceId === s.casterId));
            target.effects.push({
              skillId: s.skillId,
              kind: s.kind,
              sourceId: s.casterId,
              attribute: s.attribute,
              amount: s.amount,
              turnsLeft: Math.max(1, s.durationTurns),
            });
          }
        }
        return [{ type: 'skillUsed', skill: s }];
      }
      case 'shot': {
        const shot = payload.value;
        const last = shot.trajectory[shot.trajectory.length - 1];
        this.hasFired = true;
        const shooter = this.combatants.get(shot.shooterId);
        if (shooter) {
          shooter.actionPoints = shot.actionPoints;
          shooter.moveAllowance = shot.moveAllowance;
        }
        // Sobrou PA: o turno continua (prazo estendido pela pausa do tiro) e o atirador só age de novo quando ela acabar.
        if (shot.turnDurationMs > 0 && this.turn) {
          this.turn = { ...this.turn, durationMs: shot.turnDurationMs };
          this.lockedUntilMs = Number(shot.lockedUntilMs);
        }
        
        this.playback = { shot, startedAtMs: this.elapsedMs + this.releaseDelayMs(shot.ballId), durationMs: (last?.timeMs ?? 0) / PROJECTILE_SPEED, released: false };
        return [{ type: 'shotStarted', playback: this.playback }];
      }
      case 'snapshot': {
        const s = payload.value;
        if (this.status === 'idle') this.status = 'playing';
        this.gameId = s.gameId;
        // Quem reconecta não recebe o GameStarted: o mapa vem no snapshot (servidores antigos não o mandam e deixam 0).
        if (s.mapId > 0 || s.mapWidth > 0) {
          this.mapId = s.mapId;
          this.mapWidth = s.mapWidth;
          this.mapHeight = s.mapHeight;
          this.gravity = s.gravity;
          this.airResistance = s.airResistance;
          this.terrainHash = s.terrainHash;
          this.attackCost = s.attackCost > 0 ? s.attackCost : DEFAULT_ATTACK_COST;
          this.skillCost = s.skillCost > 0 ? s.skillCost : DEFAULT_SKILL_COST;
        }
        this.combatants.clear();
        for (const c of s.combatants) this.combatants.set(c.accountId, toView(c));
        this.craters = s.craters.map((c) => ({ ballId: c.craterId, x: c.x, y: c.y }));
        this.playback = null;
        this.hasFired = s.hasFired;
        this.lockedUntilMs = Number(s.lockedUntilMs);
        this.clock.observeServerTime(Number(s.serverTimeMs));
        this.turn = {
          number: s.turnNumber,
          accountId: s.currentPlayerId,
          wind: s.wind,
          durationMs: s.durationMs,
          startsAtMs: Number(s.turnStartsAtMs),
        };
        return [{ type: 'snapshot' }];
      }
      case 'gameFinished': {
        // O servidor já encerrou, mas o último golpe (ou a queda) ainda tem de ser visto: o resultado entra em `update`, depois da animação.
        this.pendingFinish = payload.value;
        this.finishAtMs = -1;
        return [];
      }
      default:
        return [];
    }
  }

  /** Avança o tempo: reprodução do tiro e suavização das posições. Devolve os efeitos que ficaram prontos (impacto). */
  update(dtMs: number): StateEvent[] {
    this.elapsedMs += dtMs;
    const events: StateEvent[] = [];
    const playback = this.playback;
    if (playback && !playback.released && this.elapsedMs >= playback.startedAtMs) {
      playback.released = true;
      events.push({ type: 'shotReleased', playback });
    }
    if (playback && this.elapsedMs - playback.startedAtMs >= playback.durationMs) {
      events.push(...this.flushPlayback());
    }

    for (const [id, at] of this.falls) {
      if (this.elapsedMs < at) continue;
      this.falls.delete(id);
      const c = this.combatants.get(id);
      if (c) {
        c.alive = false;
        c.blood = 0;
      }
    }
    events.push(...this.finishWhenReady());

    const factor = 1 - Math.exp(-dtMs / SMOOTHING_MS);
    for (const c of this.combatants.values()) {
      c.displayX += (c.x - c.displayX) * factor;
      c.displayY += (c.y - c.displayY) * factor;
    }
    return events;
  }

  /** Mostra o resultado quando o tiro e a queda acabaram e o tempo com o fantasma na tela passou (desistência e limite de turnos não esperam). */
  private finishWhenReady(): StateEvent[] {
    const result = this.pendingFinish;
    if (!result || this.playback || this.falls.size > 0) return [];
    if (this.finishAtMs < 0) this.finishAtMs = this.elapsedMs + (result.reason === GameEndReason.ELIMINATION ? GHOST_HOLD_MS : 0);
    if (this.elapsedMs < this.finishAtMs) return [];
    this.pendingFinish = null;
    this.status = 'finished';
    this.result = result;
    return [{ type: 'finished', result }];
  }

  /** Tempo decorrido da reprodução atual, em ms (para o renderizador posicionar o projétil). Negativo durante o preparo do atirador. */
  playbackElapsedMs(): number {
    return this.playback ? (this.elapsedMs - this.playback.startedAtMs) * PROJECTILE_SPEED : 0;
  }

  private flushPlayback(): StateEvent[] {
    const playback = this.playback;
    if (!playback) return [];
    this.playback = null;
    const shot = playback.shot;
    for (const d of shot.damages) {
      const c = this.combatants.get(d.targetId);
      if (c) {
        c.blood = d.bloodAfter;
        c.alive = !d.killed && d.bloodAfter > 0;
      }
    }
    for (const r of shot.relocated) {
      const c = this.combatants.get(r.accountId);
      if (c) {
        this.moveTo(c, r.position?.x ?? c.x, r.position?.y ?? c.y);
        // Quem teve o chão mudado pela cratera também chega aqui: a referência da mira dele mudou.
        c.tilt = r.tilt;
      }
    }
    let crater: CraterInfo | null = null;
    if (shot.dug && shot.impact) {
      crater = { ballId: shot.craterId, x: Math.round(shot.impact.x), y: Math.round(shot.impact.y) };
      this.craters.push(crater);
    }
    return [{ type: 'impact', playback, crater }];
  }

  private moveTo(c: CombatantView, x: number, y: number): void {
    const far = Math.hypot(x - c.displayX, y - c.displayY) > SNAP_DISTANCE;
    c.x = x;
    c.y = y;
    if (far) {
      c.displayX = x;
      c.displayY = y;
    }
  }

  /**
   * Espelha as regras do servidor quando um turno novo começa: o jogador cujo turno acabou perde um turno de cada efeito dele,
   * e quem vai jogar ganha um turno de cooldown em cada habilidade. (Snapshot e GameStarted já trazem os valores exatos.)
   */
  private advanceTurnTimers(nextAccountId: string): void {
    const previous = this.turn ? this.combatants.get(this.turn.accountId) : undefined;
    if (previous) {
      for (const effect of previous.effects) effect.turnsLeft--;
      previous.effects = previous.effects.filter((f) => f.turnsLeft > 0);
    }
    const next = this.combatants.get(nextAccountId);
    if (next && this.turn) {
      for (const skill of next.skills) if (skill.cooldownLeft > 0) skill.cooldownLeft--;
    }
  }

  private toTurn(t: TurnStarted): TurnInfo {
    return { number: t.turnNumber, accountId: t.accountId, wind: t.wind, durationMs: t.durationMs, startsAtMs: Number(t.serverTimeMs) };
  }

  private reset(): void {
    this.gameId = '';
    this.turn = null;
    this.hasFired = false;
    this.lockedUntilMs = 0;
    this.craters = [];
    this.playback = null;
    this.result = null;
    this.pendingFinish = null;
    this.finishAtMs = -1;
    this.falls.clear();
    this.combatants.clear();
    this.elapsedMs = 0;
  }

  /** O servidor já encerrou a partida, mas a tela ainda mostra a animação do fim (o resultado vem depois). */
  get ending(): boolean {
    return this.pendingFinish !== null;
  }
}
